import { useMemo } from "react";
import { InputTable, InputColumnTableProps, ETypeOfNotification, setNotification } from "medere-ui-framework";
import LocationActionMenu, { LocationActionItem } from "../../components/LocationActionMenu";
import { PatientCaller } from "../../../../../interface/interface";
import { savePatientCaller } from "../../../../../servicesHooks/Location/useLocationConfigService";
import { useDispatch } from "react-redux";
import useResizeListener from "../../../../../hooks/useResizeListener";
import LocationMobileActionMenu, { LocationMobileActionItem } from "../../components/LocationMobileActionMenu";

interface RowItem extends PatientCaller {
    boxSingle: PatientCaller["locationdtolist"][number];
    callerIdx: number;
    isFirstCaller: boolean;
    isFirstSector: boolean;
}

interface Props {
    callers: PatientCaller[];
    onChange: (newCallers: PatientCaller[]) => void;
    onEdit: (caller: PatientCaller) => void;
    onDelete: (caller: PatientCaller, _idx: number) => void;
}

export function PatientCallerTable({ callers, onChange, onEdit, onDelete }: Props) {
    const isMobile = useResizeListener(768);
    const dispatch = useDispatch()
    
    const rows: RowItem[] = useMemo(() => {
        if (!callers || callers.length <= 0) return []

        return callers.flatMap((caller, callerIdx) => {
            const sortedBoxes = [...caller.locationdtolist].sort((a, b) => { return (a.sector ?? 0) - (b.sector ?? 0); }); //Ordeno sectores
            const seenBySector = new Set<string>();
            return sortedBoxes.map((box, idx) => {
                const sectorKey = String(box.sector ?? '') + '::' + (box.sectorstring ?? '');
                const isFirstSector = !seenBySector.has(sectorKey);
                if (isFirstSector) seenBySector.add(sectorKey);

                return {
                    ...caller,
                    boxSingle: box,
                    callerIdx,
                    isFirstCaller: idx === 0,
                    isFirstSector
                };
            });
        });
    }, [callers]); //se modifica solo cuando callers cambia

    const toggleActive = (callerIdx: number) => { //se encarga de cambiar entre activo/inactivo el llamador. Debería hacerlo con sus boxes tambien y viceversa
        const updatedCallers = [...callers];
        const updatedItem = { ...updatedCallers[callerIdx], active: !updatedCallers[callerIdx].active };

        savePatientCaller(updatedItem).then(() => { //No inactiva consultorios/puestos de atencion, pues lo pueden usar otros llamadores y totems. Solo se desactivará la pantalla del llamador elegido
            updatedCallers[callerIdx] = updatedItem
            onChange(updatedCallers);
        }).catch((err) => {
            console.error(err)
            dispatch(setNotification({ title: "Ocurrió un error", desc: `No se pudo editar el llamador`, icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
        })
    };

    const columns: InputColumnTableProps<RowItem>[] = [
    {
        header: "Nombre",
        className: "w-1/4",
        body: (row) => (<div className="font-semibold">{row.isFirstCaller ? row.title : ""}</div>),
    },
    {
        header: "ID",
        className: "w-1/12",
        body: (row) => (row.isFirstCaller ? row.callerid : ""),
    },
    {
        header: "Sector",
        className: "w-1/5",
        body: (row) => (row.isFirstSector ? row.boxSingle.sectorstring : ""),
    },
    {
        header: "Sala / Puesto",
        className: "w-1/5 min-h-9",
        body: (row) => row.boxSingle.description,
    },
    {
        header: "Estado",
        className: "w-1/6",
        body: (row) => (row.isFirstCaller ? <span>{row.active ? "Activo" : "Inactivo"}</span> : ""),
    },
    {
        header: "",
        className: "w-1/12",
        body: (row, _idx) => {
            if (!row.isFirstCaller) return null;
            const menuItems: LocationActionItem[] = [
                { label: "Editar", icon: "pi pi-file-edit", action: () => onEdit(callers[row.callerIdx]) },
                { label: row.active ? "Desactivar" : "Activar", icon: row.active ? "pi pi-minus-circle" : "pi pi-check-circle", action: () => toggleActive(row.callerIdx) },
                { label: "Eliminar", icon: "pi pi-trash", action: () => onDelete(callers[row.callerIdx], row.callerIdx) },
            ];
            return (
                <div className="flex justify-content-end">
                    <LocationActionMenu items={menuItems} ariaLabel="Opciones del llamador" buttonId="buttonmenuitemid" />
                </div>
            );
        },
    },
    ];

    function formatCardValue(value: string | null | undefined, emptyText = "Sin datos") {
        const normalized = String(value ?? "").trim();
        return normalized !== "" ? normalized : emptyText;
    }

    if (isMobile) {
        return (
            <div className="location-mobile-card-list">
                {(callers ?? []).map((caller, index) => {
                    const sectors = Array.from(new Set((caller.locationdtolist ?? []).map(item => item.sectorstring).filter(Boolean)));
                    const boxes = (caller.locationdtolist ?? []).map(item => item.description).filter(Boolean);

                    const menuItems: LocationMobileActionItem[] = [
                        { label: "Editar", icon: "pi pi-file-edit", action: () => onEdit(caller) },
                        {
                            label: caller.active ? "Desactivar" : "Activar",
                            icon: caller.active ? "pi pi-minus-circle" : "pi pi-check-circle",
                            action: () => toggleActive(index),
                        },
                        { label: "Eliminar", icon: "pi pi-trash", action: () => onDelete(caller, index) },
                    ];

                    return (
                        <article key={`location-caller-${caller.patientcallerconfig}-${index}`} className="location-mobile-card">
                            <div className="location-mobile-card-header">
                                <h4 className="location-mobile-card-title">{formatCardValue(caller.title, "Sin nombre")}</h4>
                                <LocationMobileActionMenu items={menuItems} ariaLabel="Opciones del llamador" />
                            </div>
                            <div className="location-mobile-card-row">
                                <span className="location-mobile-card-key">ID</span>
                                <span className="location-mobile-card-value">{caller.callerid || "-"}</span>
                            </div>
                            <div className="location-mobile-card-row">
                                <span className="location-mobile-card-key">Sectores</span>
                                <span className="location-mobile-card-value">{formatCardValue(sectors.join(", "))}</span>
                            </div>
                            <div className="location-mobile-card-row">
                                <span className="location-mobile-card-key">Salas / Puestos</span>
                                <span className="location-mobile-card-value">{formatCardValue(boxes.join(", "))}</span>
                            </div>
                            <div className="location-mobile-card-row">
                                <span className="location-mobile-card-key">Estado</span>
                                <span className="location-mobile-card-value">{caller.active ? "Activo" : "Inactivo"}</span>
                            </div>
                        </article>
                    );
                })}
                {(callers ?? []).length === 0 && (
                    <p className="location-mobile-empty">No hay llamadores cargados.</p>
                )}
            </div>
        );
    }

    //border-divider hace que se vea el divider en la tabla
    return (
        <InputTable
            columnGap={4}
            headerDivClassName="font-semibold bg-bluegray-50"
            dividerClassName="my-1 border-divider"
            buttonColumnWidth={43}
            items={rows ?? []}
            columns={columns}
            divider
        />
    );
}


